/**
 * AuthCallback — handles the managed Google return flow.
 *
 * The OAuth provider redirects users to `<origin>/auth/callback?role=…#session_id=…`
 * after a successful Google sign-in. We synchronously read the session_id
 * from the URL fragment, POST it to /api/auth/social/google to exchange
 * it for our app's session_token, then route to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { clearPrivateClientState } from "@/lib/private-client-state";

export default function AuthCallback() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processedRef = useRef(false);
  const [error, setError] = useState("");
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    // 10-second stuck-state watchdog — if the token exchange hasn't
    // completed by then the backend is unresponsive; surface a
    // helpful error instead of an infinite spinner.
    const watchdog = setTimeout(() => setStuck(true), 10000);
    return () => clearTimeout(watchdog);
  }, []);

  useEffect(() => {
    // Synchronous guard — StrictMode double-invokes useEffect, but we only
    // ever want to exchange the session_id once.
    if (processedRef.current) return;
    processedRef.current = true;

    const hashParams = new URLSearchParams((loc.hash || "").replace(/^#/, ""));
    const session_id = hashParams.get("session_id");
    const queryParams = new URLSearchParams(loc.search);
    const role = queryParams.get("role") || null;

    if (!session_id) {
      setError("No session_id in the callback URL — please retry sign-in.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/social/google", { session_id, role });
        if (!data?.session_token) throw new Error("Backend did not return a session token");
        await clearPrivateClientState();
        setToken(data.session_token);
        setUser(data.user);
        // Clear the fragment so reloads don't reprocess.
        try { window.history.replaceState({}, "", loc.pathname + loc.search); } catch { /* ignore */ }
        toast.success(`Welcome${data.user?.name ? ", " + data.user.name : ""}`);
        nav("/dashboard", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed";
        setError(String(detail));
      }
    })();
  }, [loc.hash, loc.pathname, loc.search, nav, setUser]);

  if (error || stuck) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="max-w-sm w-full text-center" data-testid="auth-callback-error">
          <AlertCircle className="w-10 h-10 text-rose-600 mx-auto mb-3"/>
          <h1 className="font-display text-xl font-extrabold text-slate-900">Sign-in didn't complete</h1>
          <p className="text-sm text-slate-600 mt-2">{error || "This is taking longer than expected — please retry."}</p>
          <button onClick={() => nav("/login/landlord", { replace: true })}
            data-testid="auth-callback-retry"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-700 text-white font-semibold text-sm hover:bg-emerald-800">
            Back to sign-in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto"/>
        <div className="mt-3 text-sm text-slate-600">Signing you in…</div>
      </div>
    </div>
  );
}
